import { createElement, forwardRef, useMemo } from 'react';
import { type ImageLoader, type ImageProps } from 'next/image';

import { type ImageComponent, type ImageComponentProps } from './DefaultImageComponent';
import { createUploadcareImageLoader } from '../../utils/uploadcareImageLoader';

import styles from './UploadcareImageComponent.module.css';

type GetImageProps = (props: ImageProps) => {
  props: {
    sizes?: string;
    srcSet?: string;
  };
};

type CreateUploadcareImageComponentOptions = {
  baseLoader: ImageLoader;
  getImageProps?: GetImageProps;
  imageComponent: typeof import('next/image').default;
};

export const createUploadcareImageComponent = ({
  baseLoader,
  getImageProps,
  imageComponent,
}: CreateUploadcareImageComponentOptions): ImageComponent => {
  const UploadcareImageComponent = forwardRef<HTMLImageElement, ImageComponentProps>(
    (
      {
        uploadcareAlignment,
        uploadcareAspectRatio,
        uploadcareMediaAspectRatio,
        uploadcareMediaQuery,
        uploadcareOperation,
        loader: providedLoader,
        ...props
      },
      ref,
    ) => {
      const loader = useMemo(() => {
        if (providedLoader) return providedLoader;

        if (!uploadcareAspectRatio || !uploadcareOperation) return baseLoader;

        return createUploadcareImageLoader(baseLoader, {
          alignment: uploadcareAlignment,
          aspectRatio: uploadcareAspectRatio,
          operation: uploadcareOperation,
        });
      }, [providedLoader, uploadcareAlignment, uploadcareAspectRatio, uploadcareOperation]);

      const mediaLoader = useMemo(() => {
        if (providedLoader || !uploadcareMediaAspectRatio || !uploadcareMediaQuery || !uploadcareOperation) return undefined;

        return createUploadcareImageLoader(baseLoader, {
          alignment: uploadcareAlignment,
          aspectRatio: uploadcareMediaAspectRatio,
          operation: uploadcareOperation,
        });
      }, [providedLoader, uploadcareAlignment, uploadcareMediaAspectRatio, uploadcareMediaQuery, uploadcareOperation]);

      const image = createElement(imageComponent, { ...props, loader, ref });

      if (!getImageProps || !mediaLoader) return image;

      const { props: mediaImageProps } = getImageProps({ ...props, loader: mediaLoader });

      return (
        <picture className={styles.picture}>
          <source media={uploadcareMediaQuery} sizes={mediaImageProps.sizes} srcSet={mediaImageProps.srcSet} />
          {image}
        </picture>
      );
    },
  );

  UploadcareImageComponent.displayName = 'UploadcareImageComponent';

  return UploadcareImageComponent;
};
